import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  useExtensionVersions,
  useExtensionVersionsPagination,
} from '@/hooks';

function VersionsTable() {
  const { data, isLoading } = useExtensionVersions();
  const [{ page, limit }, setPagination] = useExtensionVersionsPagination();

  const rows = data?.rows ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  return (
    <div className='flex flex-col gap-3'>
      <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Accounts</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Last seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className='h-24 text-center text-muted-foreground'>
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className='h-24 text-center text-muted-foreground'>
                  No data yet. Users must make at least one authenticated request with the extension.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.user_id}>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>
                    <div className='flex flex-col gap-1'>
                      {row.accounts.map((a) => (
                        <span key={a.account_id} className='text-xs'>
                          <span className='font-medium'>{a.account_name ?? String(a.account_id)}</span>
                          <span className='ml-1 text-muted-foreground'>({a.account_id})</span>
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className='font-mono'>{row.version ?? '—'}</TableCell>
                  <TableCell className='text-muted-foreground'>
                    {new Date(row.last_seen_at).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
      </Table>

      {totalPages > 1 && (
        <div className='flex items-center justify-end gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={page <= 1}
            onClick={() => setPagination({ page: page - 1, limit })}
          >
            Previous
          </Button>
          <span className='text-sm text-muted-foreground'>
            Page {page} of {totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={page >= totalPages}
            onClick={() => setPagination({ page: page + 1, limit })}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ExtensionVersionsPage() {
  return (
    <div className='flex h-full flex-col gap-6 p-4'>
      <div className='flex items-center gap-3'>
        <div>
          <h1 className='text-base font-semibold'>Extension Versions</h1>
          <p className='text-xs text-muted-foreground'>Track user version adoption across all accounts.</p>
        </div>
      </div>

      <VersionsTable />
    </div>
  );
}
